import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, Wrench, Calendar, AlertTriangle } from "lucide-react";
import Money from "@/components/Money";

/**
 * Fleet utilization dashboard for admin. One read of `/admin/fleet-utilization` powers
 * a 5-KPI strip + a sortable row table. KPIs cover the things admins care about most
 * day-to-day: total bookings, revenue, average utilization, issue count, overage rate.
 *
 * Sortability is purely client-side (the payload is bounded by fleet size — a few
 * hundred rows tops). Window picker lets admins flip between 30/90/180/365 days.
 *
 * No charts library — we lean on a "data-bar" CSS trick (a colored backdrop with width
 * scaled to the row's utilization %) so the table itself doubles as a sparkline.
 */
export default function FleetUtilizationPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [sortKey, setSortKey] = useState("revenue");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const load = (w) => {
    setLoading(true);
    api.get(`/admin/fleet-utilization?window_days=${w}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(days); }, [days]);

  const rows = useMemo(() => {
    if (!data) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => {
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      if (typeof av === "string") return sortDir === "desc" ? bv.localeCompare(av) : av.localeCompare(bv);
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSortKey(k); setSortDir("desc"); }
  };

  if (!data) return <div className="text-sm text-neutral-500 py-8">Loading fleet…</div>;

  return (
    <div data-testid="fleet-utilization-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-medium">Fleet performance</h3>
          <p className="text-xs text-neutral-500">Last {data.window_days} days · {data.fleet_size} cars ({data.active_cars} active)</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-36 rounded-full h-9" data-testid="fleet-window-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="fleet-kpis">
        <Kpi icon={Calendar} label="Bookings" value={data.totals.bookings} tone="indigo" testId="kpi-bookings" />
        <Kpi icon={TrendingUp} label="Revenue" value={<Money amount={data.totals.revenue} />} tone="emerald" testId="kpi-revenue" />
        <Kpi icon={BarChart3} label="Avg utilization" value={`${data.totals.average_utilization_pct}%`} tone="violet" testId="kpi-utilization" />
        <Kpi icon={Wrench} label="Issues logged" value={data.totals.issues_logged} tone="slate" testId="kpi-issues" />
        <Kpi icon={AlertTriangle} label="With mileage overage" value={data.totals.bookings_with_overage} tone="amber" testId="kpi-overage" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border" data-testid="fleet-table-wrapper">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <Th k="make" cur={sortKey} dir={sortDir} onClick={toggleSort}>Vehicle</Th>
              <Th k="location" cur={sortKey} dir={sortDir} onClick={toggleSort}>Location</Th>
              <Th k="bookings" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Bookings</Th>
              <Th k="utilization_pct" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Utilization</Th>
              <Th k="revenue" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Revenue</Th>
              <Th k="avg_km_per_day" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Avg km/day</Th>
              <Th k="issues_logged" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Issues</Th>
              <Th k="bookings_with_overage" cur={sortKey} dir={sortDir} onClick={toggleSort} right>Overage</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-neutral-400 text-sm">No data yet — no confirmed bookings in this window.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.car_id} className="border-t border-border hover:bg-neutral-50/60" data-testid={`fleet-row-${r.car_id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.make} {r.model}</div>
                  <div className="text-xs text-neutral-400">{r.year}{r.is_active ? "" : " · inactive"}</div>
                </td>
                <td className="px-3 py-2 text-xs text-neutral-600">{r.location || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.bookings}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-2 tabular-nums">
                    <span className="text-xs">{r.utilization_pct}%</span>
                    <div className="w-16 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                      <div className={`h-full ${r.utilization_pct >= 60 ? "bg-emerald-500" : r.utilization_pct >= 30 ? "bg-amber-500" : "bg-rose-400"}`} style={{ width: `${Math.min(100, r.utilization_pct)}%` }} />
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-medium"><Money amount={r.revenue} /></td>
                <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{r.avg_km_per_day != null ? `${r.avg_km_per_day} km` : "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.issues_logged > 0 ? <Badge variant="secondary" className="text-[10px]">{r.issues_logged}</Badge> : <span className="text-neutral-300">0</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.bookings_with_overage > 0 ? <Badge className="bg-amber-600 text-[10px]">{r.bookings_with_overage}</Badge> : <span className="text-neutral-300">0</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <div className="text-xs text-neutral-400 mt-2">Refreshing…</div>}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "indigo", testId }) {
  const bg = { indigo: "bg-indigo-50 text-indigo-700", emerald: "bg-emerald-50 text-emerald-700", violet: "bg-violet-50 text-violet-700", slate: "bg-slate-100 text-slate-700", amber: "bg-amber-50 text-amber-700" }[tone];
  return (
    <div className="rounded-2xl border border-border p-3 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className={`h-8 w-8 rounded-xl flex items-center justify-center ${bg}`}><Icon className="h-4 w-4" /></div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-neutral-500 truncate">{label}</div>
          <div className="text-lg font-semibold tabular-nums">{value}</div>
        </div>
      </div>
    </div>
  );
}

function Th({ children, k, cur, dir, onClick, right }) {
  const active = cur === k;
  return (
    <th
      className={`px-3 py-2 ${right ? "text-right" : "text-left"} cursor-pointer select-none hover:text-neutral-900 ${active ? "text-neutral-900" : ""}`}
      onClick={() => onClick(k)}
      data-testid={`fleet-th-${k}`}
    >
      <span className="inline-flex items-center gap-1">{children}{active && <span className="text-[10px]">{dir === "desc" ? "▼" : "▲"}</span>}</span>
    </th>
  );
}
